import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { fDate } from 'src/utils/format-time';
import { useTranslate } from 'src/locales';
import Iconify from 'src/components/iconify';
import {GetProfileOutput} from "@/types";

// ----------------------------------------------------------------------

type Props = {
  profile: GetProfileOutput;
  userName: string;
};

// ----------------------------------------------------------------------

export function UserProfileCard({profile, userName}: Props) {
  const { t } = useTranslate();
  const userInitial = userName.charAt(0).toUpperCase();

  return (
    <Card>
      <CardContent>
        {/* Header profilu */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'center', sm: 'flex-start' },
            gap: 3,
          }}
        >
          <Avatar
            src={profile.photo?.url || undefined}
            alt={userName}
            sx={{
              width: 120,
              height: 120,
              bgcolor: 'primary.main',
              fontSize: '3rem',
            }}
          >
            {!profile.photo?.url && userInitial}
          </Avatar>

          <Box sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}>
            <Typography variant="h4" gutterBottom sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              {userName}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, textAlign: { xs: 'center', sm: 'left' } }}
            >
              {t('page.user.profile.joined')}: {profile.createdAt ? fDate(profile.createdAt * 1000) : '-'}
            </Typography>

            {/* Social Media Links - nad opisem */}
            {(profile.facebookUrl || profile.twitterUrl || profile.instagramUrl || profile.youtubeUrl) && (
              <Box sx={{ display: 'flex', gap: 1, mb: 2, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                {profile.facebookUrl && (
                  <IconButton
                    component="a"
                    href={profile.facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {color: 'primary.main'},
                    }}
                  >
                    <Iconify icon="eva:facebook-fill" width={24} />
                  </IconButton>
                )}

                {profile.instagramUrl && (
                  <IconButton
                    component="a"
                    href={profile.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {color: 'primary.main'},
                    }}
                  >
                    <Iconify icon="ant-design:instagram-filled" width={24} />
                  </IconButton>
                )}

                {profile.twitterUrl && (
                  <IconButton
                    component="a"
                    href={profile.twitterUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {color: 'primary.main'},
                    }}
                  >
                    <Iconify icon="eva:twitter-fill" width={24} />
                  </IconButton>
                )}

                {profile.youtubeUrl && (
                  <IconButton
                    component="a"
                    href={profile.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:hover': {color: 'primary.main'},
                    }}
                  >
                    <Iconify icon="eva:youtube-fill" width={24} />
                  </IconButton>
                )}
              </Box>
            )}

            {/* O mnie - z prawej strony obok avatara na desktop */}
            {profile.about && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: { xs: 12, sm: 6 },
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {profile.about}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
